import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import ChevronDownSmallIcon from '@assets/icons/ChevronDownSmallIcon';
import styles from './Button.module.scss';

// ============================================
// TYPES
// ============================================

export type ButtonVariant = 'filled' | 'outline' | 'outline-neutral' | 'ghost' | 'link' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style variant */
  variant?: ButtonVariant;
  /** Button size */
  size?: ButtonSize;
  /** Icon element rendered before the label */
  leftIcon?: ReactNode;
  /** Icon element rendered after the label */
  rightIcon?: ReactNode;
  /** Render as icon-only button (hides label, square aspect) */
  iconOnly?: boolean;
  /** Show loading spinner and disable interaction */
  loading?: boolean;
  /** Stretch to fill parent width */
  fullWidth?: boolean;
}

// ============================================
// HELPERS
// ============================================

function buildClassName(
  variant: ButtonVariant,
  size: ButtonSize,
  iconOnly: boolean,
  loading: boolean,
  fullWidth: boolean,
  disabled: boolean,
  className?: string,
): string {
  // CSS Modules class names are camelCased; map the hyphenated variant string.
  const variantClass = variant === 'outline-neutral' ? styles.outlineNeutral : styles[variant];
  const classes = [
    styles.button,
    variantClass,
    styles[size],
  ];

  if (iconOnly) classes.push(styles.iconOnly);
  if (loading) classes.push(styles.loading);
  if (fullWidth) classes.push(styles.fullWidth);
  if (disabled) classes.push(styles.disabled);
  if (className) classes.push(className);

  return classes.filter(Boolean).join(' ');
}

// ============================================
// COMPONENT
// ============================================

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'filled',
      size = 'md',
      leftIcon,
      rightIcon,
      iconOnly = false,
      loading = false,
      fullWidth = false,
      disabled = false,
      children,
      className,
      type = 'button',
      ...rest
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        className={buildClassName(variant, size, iconOnly, loading, fullWidth, isDisabled, className)}
        aria-busy={loading || undefined}
        {...rest}
      >
        {leftIcon && <span className={styles.icon}>{leftIcon}</span>}

        {!iconOnly && children && (
          <span className={styles.label}>{children}</span>
        )}

        {iconOnly && children && (
          <span className={styles.icon}>{children}</span>
        )}

        {rightIcon && !iconOnly && <span className={styles.icon}>{rightIcon}</span>}

        {loading && <span className={styles.spinner} />}
      </button>
    );
  },
);

Button.displayName = 'Button';

export default Button;

// ============================================
// COMBO BUTTON
// ============================================

export interface ComboButtonProps {
  /** Props for the main (left) button */
  mainProps: ButtonProps;
  /** Props for the dropdown trigger (right chevron) */
  triggerProps?: ButtonHTMLAttributes<HTMLButtonElement>;
  /** Size applied to both halves */
  size?: ButtonSize;
  /** Visual variant — 'filled' or 'danger' */
  variant?: Extract<ButtonVariant, 'filled' | 'danger'>;
  /** Icon for the dropdown trigger (defaults to a chevron-down) */
  triggerIcon?: ReactNode;
  /** Whether the main button is loading */
  loading?: boolean;
  /** Whether both halves are disabled */
  disabled?: boolean;
}

export const ComboButton = forwardRef<HTMLDivElement, ComboButtonProps>(
  (
    {
      mainProps,
      triggerProps,
      size = 'md',
      variant = 'filled',
      triggerIcon,
      loading = false,
      disabled = false,
    },
    ref,
  ) => {
    const chevron = triggerIcon ?? <ChevronDownSmallIcon />;

    return (
      <div ref={ref} className={styles.comboWrapper}>
        <Button
          {...mainProps}
          variant={variant}
          size={size}
          loading={loading}
          disabled={disabled}
          className={`${mainProps.className ?? ''} ${styles.comboMain}`.trim()}
        />
        <span className={styles.comboDivider} />
        <button
          type="button"
          disabled={disabled || loading}
          {...triggerProps}
          className={buildClassName(variant, size, true, false, false, disabled || loading, `${triggerProps?.className ?? ''} ${styles.comboTrigger}`.trim())}
          aria-label="More options"
        >
          <span className={styles.icon}>{chevron}</span>
        </button>
      </div>
    );
  },
);

ComboButton.displayName = 'ComboButton';
